import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Autocomplete,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { event as eventApi, user as userApi } from '../../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../../components/auth/AuthModule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EuroIcon from '@mui/icons-material/Euro';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';

const EventDetailModal = ({ open, onClose, eventId, onEventUpdated }) => {
  const { t } = useTranslation('events');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [hasPaid, setHasPaid] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    endDate: '',
    price: 0,
    signupLink: '',
    maxParticipants: '',
    isPublic: true
  });
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (open && eventId) {
      fetchEventDetails();
    }
  }, [open, eventId]);

  useEffect(() => {
    if (inviteDialogOpen) {
      fetchAvailableUsers();
    }
  }, [inviteDialogOpen]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const data = await eventApi.getById(eventId);
      setEvent(data);
      
      // Initialize edit form data
      setEditFormData({
        title: data.title || '',
        description: data.description || '',
        location: data.location || '',
        date: data.date ? format(new Date(data.date), 'yyyy-MM-dd') : '',
        endDate: data.endDate ? format(new Date(data.endDate), 'yyyy-MM-dd') : '',
        price: data.price || 0,
        signupLink: data.signupLink || '',
        maxParticipants: data.maxParticipants || '',
        isPublic: data.isPublic !== undefined ? data.isPublic : true
      });
      
      // Set current user's attendance status
      if (user && data.participants) {
        const userParticipation = data.participants.find(p => p.user._id === user._id);
        if (userParticipation) {
          setAttendanceStatus(userParticipation.status);
          setHasPaid(userParticipation.hasPaid || false);
        } else {
          setAttendanceStatus(null);
          setHasPaid(false);
        }
      }

      // Set invited users for private events
      if (data.invitedUsers) {
        setInvitedUsers(data.invitedUsers);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error(t('errors.loadingEventDetails'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await userApi.getUsers();
      
      // Filter out users who are already participants or invited
      const participantIds = event.participants?.map(p => p.user._id) || [];
      const invitedIds = invitedUsers.map(u => u._id);
      
      const filteredUsers = users.filter(u => 
        !participantIds.includes(u._id) && 
        !invitedIds.includes(u._id) &&
        u._id !== user._id
      );
      
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errors.loadingUsers'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAttendanceChange = async (event, newStatus) => {
    if (newStatus === null) return;
    
    try {
      setUpdatingStatus(true);
      
      // Check if user is already a participant
      const isParticipant = attendanceStatus !== null;
      
      if (isParticipant) {
        // Update existing participation
        await eventApi.updateParticipantStatus(eventId, { 
          status: newStatus,
          hasPaid: hasPaid 
        });
        toast.success(t('success.attendanceUpdated'));
      } else {
        // Add user as a new participant
        await eventApi.addParticipant(eventId, { 
          status: newStatus,
          hasPaid: hasPaid 
        });
        toast.success(t('success.joinedEvent'));
      }
      
      fetchEventDetails();
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error(t('errors.updatingAttendance'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusChange = async () => {
    if (!attendanceStatus) return;
    
    try {
      setUpdatingStatus(true);
      const newPaymentStatus = !hasPaid;
      
      await eventApi.updateParticipantStatus(eventId, { 
        status: attendanceStatus,
        hasPaid: newPaymentStatus 
      });
      
      setHasPaid(newPaymentStatus);
      toast.success(newPaymentStatus 
        ? t('success.paymentMarkedAsPaid') 
        : t('success.paymentMarkedAsUnpaid'));
      
      fetchEventDetails();
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error(t('errors.updatingPaymentStatus'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      
      // Format the data
      const eventData = {
        ...editFormData,
        price: editFormData.price ? Number(editFormData.price) : 0,
        maxParticipants: editFormData.maxParticipants ? Number(editFormData.maxParticipants) : null
      };
      
      await eventApi.update(eventId, eventData);
      toast.success(t('success.eventUpdated'));
      setIsEditMode(false);
      fetchEventDetails();
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(t('errors.updatingEvent'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await eventApi.delete(eventId);
      toast.success(t('success.eventDeleted'));
      setDeleteDialogOpen(false);
      onClose();
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(t('errors.deletingEvent'));
    }
  };

  const handleInviteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setInviting(true);
      await eventApi.inviteUser(eventId, { userId: selectedUser._id });
      toast.success(t('success.userInvited', { user: selectedUser.name }));
      setSelectedUser(null);
      fetchEventDetails();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(t('errors.invitingUser'));
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveInvitedUser = async (userId) => {
    try {
      // Check if the user is also a participant
      const isParticipant = event.participants.some(p => p.user._id === userId);
      
      // Remove from invited users
      await eventApi.removeInvitedUser(eventId, userId);
      
      // If they are also a participant, remove them from participants list
      if (isParticipant) {
        await eventApi.removeSpecificParticipant(eventId, userId);
        toast.success(t('success.userRemovedFromEvent'));
      } else {
        toast.success(t('success.userUninvited'));
      }
      
      fetchEventDetails();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error removing invited user:', error);
      toast.error(t('errors.removingInvitedUser'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'attending':
        return 'success';
      case 'not_attending':
        return 'error';
      case 'maybe':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!event) return null;

  const isEventPast = new Date(event.date) < new Date();
  const isCreator = user && event.createdBy && user._id === event.createdBy._id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {isEditMode ? (
            <Typography variant="h5" component="div">
              {t('eventDetail.editTitle')}
            </Typography>
          ) : (
            <Typography variant="h5" component="div">
              {event.title}
            </Typography>
          )}
          <Box>
            {isCreator && !isEditMode && (
              <>
                <IconButton 
                  color="primary" 
                  onClick={() => setIsEditMode(true)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  color="error" 
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{ mr: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {!isEditMode && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Chip 
              label={isEventPast ? t('eventList.past') : t('eventList.upcoming')} 
              color={isEventPast ? 'default' : 'primary'} 
              size="small" 
            />
            <Chip 
              label={event.isPublic ? t('eventDetail.public') : t('eventDetail.private')} 
              color={event.isPublic ? 'success' : 'default'} 
              size="small" 
            />
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        {isEditMode ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label={t('createEvent.fields.title')}
                fullWidth
                required
                value={editFormData.title}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('createEvent.fields.description')}
                fullWidth
                required
                multiline
                rows={4}
                value={editFormData.description}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="location"
                label={t('createEvent.fields.location')}
                fullWidth
                required
                value={editFormData.location}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="date"
                label={t('createEvent.fields.date')}
                type="date"
                fullWidth
                required
                value={editFormData.date}
                onChange={handleEditChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="endDate"
                label={t('createEvent.fields.endDate')}
                type="date"
                fullWidth
                value={editFormData.endDate}
                onChange={handleEditChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="price"
                label={t('createEvent.fields.price')}
                type="number"
                fullWidth
                value={editFormData.price}
                onChange={handleEditChange}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="maxParticipants"
                label={t('createEvent.fields.maxParticipants')}
                type="number"
                fullWidth
                value={editFormData.maxParticipants}
                onChange={handleEditChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="signupLink"
                label={t('createEvent.fields.signupLink')}
                fullWidth
                value={editFormData.signupLink}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.isPublic}
                    onChange={handleEditChange}
                    name="isPublic"
                    color="primary"
                  />
                }
                label={t('createEvent.fields.isPublic')}
              />
              {!editFormData.isPublic && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  {t('eventDetail.privateEventNote')}
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body1" paragraph>
                {event.description}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1">{event.location}</Typography>
              </Box>
              
              <Box display="flex" alignItems="center" mb={1}>
                <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  {format(new Date(event.date), 'PPP')}
                  {event.endDate && ` - ${format(new Date(event.endDate), 'PPP')}`}
                </Typography>
              </Box>
              
              {event.price > 0 && (
                <Box display="flex" alignItems="center" mb={1}>
                  <EuroIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">{event.price} €</Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  {event.participants?.length || 0} 
                  {event.maxParticipants ? ` / ${event.maxParticipants}` : ''} {t('eventDetail.participants')}
                </Typography>
              </Box>
              
              {event.signupLink && (
                <Box display="flex" alignItems="center" mb={1}>
                  <LinkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <a href={event.signupLink} target="_blank" rel="noopener noreferrer">
                      {t('eventDetail.signupLink')}
                    </a>
                  </Typography>
                </Box>
              )}
            </Grid>
            
            {/* Private Event Invitations Section */}
            {!event.isPublic && isCreator && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('eventDetail.invitedUsers')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteDialogOpen(true)}
                  >
                    {t('eventDetail.inviteUsers')}
                  </Button>
                </Box>
                
                {invitedUsers && invitedUsers.length > 0 ? (
                  <List>
                    {invitedUsers.map((invitedUser) => (
                      <Paper key={invitedUser._id} elevation={1} sx={{ mb: 1 }}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar>{invitedUser.name.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={invitedUser.name} 
                            secondary={invitedUser.email}
                          />
                          {isCreator && (
                            <ListItemSecondaryAction>
                              <IconButton 
                                edge="end" 
                                color="error"
                                onClick={() => handleRemoveInvitedUser(invitedUser._id)}
                              >
                                <PersonRemoveIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('eventDetail.noInvitedUsers')}
                  </Typography>
                )}
              </Grid>
            )}
            
            {!isEventPast && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('attendance.yourStatus')}
                </Typography>
                <ToggleButtonGroup
                  value={attendanceStatus}
                  exclusive
                  onChange={handleAttendanceChange}
                  disabled={updatingStatus || (!event.isPublic && !invitedUsers.some(u => u._id === user._id) && !isCreator)}
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="attending" color="success">
                    {t('attendance.attending')}
                  </ToggleButton>
                  <ToggleButton value="maybe" color="warning">
                    {t('attendance.maybe')}
                  </ToggleButton>
                  <ToggleButton value="not_attending" color="error">
                    {t('attendance.notAttending')}
                  </ToggleButton>
                </ToggleButtonGroup>
                {updatingStatus && <CircularProgress size={24} sx={{ ml: 2 }} />}
                
                {/* Payment status toggle for events with price */}
                {event.price > 0 && attendanceStatus === 'attending' && (
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hasPaid}
                          onChange={handlePaymentStatusChange}
                          disabled={updatingStatus}
                          color="success"
                        />
                      }
                      label={hasPaid ? t('attendance.paid') : t('attendance.notPaid')}
                    />
                  </Box>
                )}
                
                {!event.isPublic && !invitedUsers.some(u => u._id === user._id) && !isCreator && (
                  <Typography variant="body2" color="text.secondary">
                    {t('eventDetail.privateEventRestriction')}
                  </Typography>
                )}
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('eventDetail.participantsList')}
              </Typography>
              {event.participants && event.participants.length > 0 ? (
                <List>
                  {event.participants.map((participant) => (
                    <ListItem key={participant.user._id}>
                      <ListItemAvatar>
                        <Avatar>{participant.user.name ? participant.user.name.charAt(0) : participant.user.username.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={participant.user.name || participant.user.username} 
                        secondary={
                          <Box>
                            <Chip 
                              label={t(`attendance.${participant.status}`)} 
                              size="small" 
                              color={getStatusColor(participant.status)}
                              sx={{ mr: 1 }}
                            />
                            {event.price > 0 && participant.status === 'attending' && (
                              <Chip 
                                label={participant.hasPaid ? t('attendance.paid') : t('attendance.notPaid')} 
                                size="small" 
                                color={participant.hasPaid ? 'success' : 'default'}
                                variant={participant.hasPaid ? 'filled' : 'outlined'}
                              />
                            )}
                          </Box>
                        }
                      />
                      {isCreator && participant.user._id !== user._id && (
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            color="error"
                            onClick={() => handleRemoveInvitedUser(participant.user._id)}
                          >
                            <PersonRemoveIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t('eventDetail.noParticipants')}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {isEditMode ? (
          <>
            <Button onClick={() => setIsEditMode(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              variant="contained" 
              color="primary"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : t('common.save')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('eventDetail.confirmDelete')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteEvent} color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Users Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('eventDetail.inviteUsers')}</DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('eventDetail.inviteUsersDescription')}
            </Typography>
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) => option.name}
              loading={loadingUsers}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('eventDetail.selectUser')}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                      {option.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
            />
          </Box>

          {invitedUsers && invitedUsers.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                {t('eventDetail.currentlyInvited')}
              </Typography>
              <List>
                {invitedUsers.map((invitedUser) => (
                  <Paper key={invitedUser._id} elevation={1} sx={{ mb: 1 }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>{invitedUser.name.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={invitedUser.name} 
                        secondary={invitedUser.email}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          color="error"
                          onClick={() => handleRemoveInvitedUser(invitedUser._id)}
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            {t('common.close')}
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained" 
            color="primary"
            disabled={!selectedUser || inviting}
          >
            {inviting ? <CircularProgress size={24} /> : t('eventDetail.invite')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EventDetailModal; 